<script setup lang="ts">
import Loader from "@/dsComponents/Loader.vue";

interface ArlyAiButtonProps {
    label: string;
    icon: string;
    loading?: boolean;
    size?: "sm" | "md";
}

// biome-ignore lint/correctness/noUnusedVariables: Init Biome
const props = withDefaults(defineProps<ArlyAiButtonProps>(), {
    size: "md",
    loading: false,
});

const emit = defineEmits(["click"]);

const clickEvent = (event: MouseEvent) => {
    emit("click", event);
};
</script>

<template>
    <button
        :class="['arly-ai-button', `arly-ai-button--${size}`, { 'arly-ai-button--loading': loading }]"
        :disabled="loading"
        @click="clickEvent"
    >
        <i v-if="!loading" :class="`ti ti-${icon} arly-ai-button__icon`" />
        <Loader v-else size="sm" />
        <span :class="['arly-ai-button__label', size === 'sm' ? 'typo-xs-medium' : 'typo-sm-medium']">{{ label }}</span>
    </button>
</template>

<style lang="scss" scoped>
.arly-ai-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-xs, 4px);
    padding: 0 var(--spacing-md, 8px);
    border: 1px solidd var(--purple-light, #e9dafbff);
    background: linear-gradient(273deg, var(--purple-lighter, #f4edfd) 5.27%, var(--blue-lighter, #ebefff) 95.2%);
    box-shadow: 0 1px 18px 0 rgba(233, 218, 251, 0.4);
    transition: all 0.3s ease-in-out;

    &--sm {
        height: 28px;
        border-radius: var(--radius-sm, 6px);

        .arly-ai-button__icon {
            font-size: 14px;
        }
    }

    &--md {
        height: 36px;
        border-radius: var(--radius-md, 8px);

        .arly-ai-button__icon {
            font-size: 16px;
        }
    }

    &__label {
        color: var(--blue-dark, #042aaf);
        padding: 0 var(--spacing-xs, 4px);
    }

    &__icon {
        color: var(--blue-dark, #042aaf);
    }

    &:hover:not(&--loading) {
        background: linear-gradient(273deg, var(--purple-lighter, #f4edfd) 49.79%, var(--blue-lighter, #ebefff) 95.2%);

        .arly-ai-button__icon {
            color: var(--blue-base, #2051f8);
        }

        .arly-ai-button__label {
            color: var(--blue-base, #2051f8);
        }
    }

    &--loading {
        cursor: default;
        pointer-events: none;

        .arly-ai-button__label {
            color: var(--vibrant-blue-blue-400, #99b0ff);
        }
    }
}
</style>
